import { Maximize2, Minimize2, X } from 'lucide-react';
import {
  createContext,
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  useContext,
  useId
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'v5/design-system/Button';
import { cn } from 'v5/platform/dom/cn';
import { ScrollShadowWrapper } from 'v5/primitives/ScrollShadowWrapper';
import { useSplitView } from './splitViewContext';

const SplitViewColumnContext = createContext<{
  onClose: () => void;
  titleId: string;
} | null>(null);

function useSplitViewColumn() {
  const context = useContext(SplitViewColumnContext);
  if (!context) {
    throw new Error('SplitViewColumn parts must be inside SplitViewColumn');
  }
  return context;
}

export interface SplitViewColumnProps extends HTMLAttributes<HTMLElement> {
  onClose: () => void;
}

/**
 * The split view's column right of the content card (`SplitViewLayout`): a card of its own beside
 * the content, which narrows for it instead of being covered. The width switches in one frame, so
 * the content reflows once, and the column's content comes in by transform and opacity only.
 * Maximized, or on a layout too narrow for both, it is a modal dialog over the page instead,
 * labelled by its title.
 * Escape with the focus inside closes it; a sheet it opened over the content closes first.
 * Compose it from `Header` (with `Title`, `Maximize` and `Close`), `Body` (scrolls) and `Footer`.
 */
const SplitViewColumnRoot = forwardRef<HTMLElement, SplitViewColumnProps>(
  ({ onClose, className, children, onKeyDown, ...props }, ref) => {
    const titleId = useId();
    const { inDialog, flush } = useSplitView();
    return (
      <SplitViewColumnContext.Provider value={{ onClose, titleId }}>
        <aside
          ref={ref}
          aria-labelledby={titleId}
          role={inDialog ? 'dialog' : undefined}
          aria-modal={inDialog ? true : undefined}
          className={cn(
            // `isolate`: what the content raises stays inside the column
            'relative isolate flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl bg-bg-default text-fg-default',
            // the frame drawn over the content, not as a border on the column's own ground; in a
            // dialog the dialog is the frame
            'after:pointer-events-none after:absolute after:inset-0 after:z-10 after:rounded-[inherit] after:border after:border-border-default',
            inDialog && 'rounded-none after:hidden',
            // a panel filling the container: square, on the subtle ground, a line at its left
            flush &&
              !inDialog &&
              'rounded-none bg-bg-subtle after:border-y-0 after:border-r-0',
            className
          )}
          onKeyDown={event => {
            onKeyDown?.(event);
            // React bubbles events from portals (a popover's, a sheet's) through here too
            if (
              event.key === 'Escape' &&
              !event.defaultPrevented &&
              event.target instanceof Node &&
              event.currentTarget.contains(event.target)
            ) {
              event.stopPropagation();
              onClose();
            }
          }}
          {...props}
        >
          <div className="flex min-h-0 flex-1 flex-col duration-200 animate-in fade-in slide-in-from-right-4">
            {children}
          </div>
        </aside>
      </SplitViewColumnContext.Provider>
    );
  }
);
SplitViewColumnRoot.displayName = 'SplitViewColumn';

const SplitViewColumnHeader = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex min-h-12 shrink-0 items-center gap-2 border-b border-border-default px-4 py-2',
      className
    )}
    {...props}
  />
));
SplitViewColumnHeader.displayName = 'SplitViewColumn.Header';

function SplitViewColumnTitle({
  className,
  children
}: {
  className?: string;
  children: ReactNode;
}) {
  const { titleId } = useSplitViewColumn();
  return (
    <h2
      id={titleId}
      // takes the row, so the actions after it stand at the right edge
      className={cn('min-w-0 flex-1 truncate text-sm font-semibold', className)}
    >
      {children}
    </h2>
  );
}

/** Opens the column in a dialog over the page, or brings it back beside the card; absent where the layout decides. */
function SplitViewColumnMaximize({ className }: { className?: string }) {
  const { t } = useTranslation('patterns', { keyPrefix: 'SplitViewColumn' });
  const { canMaximize, maximized, onMaximizedChange } = useSplitView();
  if (!canMaximize || !onMaximizedChange) return null;
  return (
    <Button
      variant="ghost"
      layout="icon"
      size="sm"
      shape="rounded"
      aria-label={t(maximized ? 'restore' : 'maximize')}
      className={className}
      onClick={() => onMaximizedChange(!maximized)}
    >
      {maximized ? <Minimize2 /> : <Maximize2 />}
    </Button>
  );
}

function SplitViewColumnClose({ className }: { className?: string }) {
  const { t } = useTranslation('patterns', { keyPrefix: 'SplitViewColumn' });
  const { onClose } = useSplitViewColumn();
  return (
    <Button
      variant="ghost"
      layout="icon"
      size="sm"
      shape="rounded"
      aria-label={t('close')}
      className={className}
      onClick={onClose}
    >
      <X />
    </Button>
  );
}

/** Scrolls between the header and the footer; a column, so a list inside can fill it. */
function SplitViewColumnBody({
  className,
  children
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <ScrollShadowWrapper
      edge="soft"
      className={cn('min-h-0 flex-1', className)}
      scrollerClassName="flex flex-col"
    >
      {children}
    </ScrollShadowWrapper>
  );
}

const SplitViewColumnFooter = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex shrink-0 items-center gap-2 border-t border-border-default px-4 py-3',
      className
    )}
    {...props}
  />
));
SplitViewColumnFooter.displayName = 'SplitViewColumn.Footer';

export const SplitViewColumn = Object.assign(SplitViewColumnRoot, {
  Header: SplitViewColumnHeader,
  Title: SplitViewColumnTitle,
  Maximize: SplitViewColumnMaximize,
  Close: SplitViewColumnClose,
  Body: SplitViewColumnBody,
  Footer: SplitViewColumnFooter
});
